import * as THREE from 'three';
import { GameObject, ObjectType } from '../objects/GameObject';
import { DynamicObject } from '../objects/dynamic/DynamicObject';
import { SpatialManager } from '../optimization/SpatialManager';
import { LODToaster } from '../objects/dynamic/LODToaster';

/**
 * System for handling collision detection and response
 */
export class CollisionSystem {
  private static instance: CollisionSystem | null = null;
  
  private staticObjects: Set<GameObject> = new Set();
  private dynamicObjects: Set<DynamicObject> = new Set();
  private spatialManager: SpatialManager | null = null;
  
  // Optimization: Pre-allocate vectors for calculations
  private static readonly tempDir = new THREE.Vector3();
  private static readonly tempVelocity = new THREE.Vector3();
  private static readonly tempAvoidance = new THREE.Vector3();
  
  // Configuration
  private readonly collisionCheckRadius: number = 10; // Only check objects within this distance
  private readonly avoidanceAngle: number = Math.PI / 2; // 90 degrees
  
  // Timing-related
  private currentTime: number = 0;
  
  private constructor() {
    console.log('CollisionSystem initialized');
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): CollisionSystem {
    if (!CollisionSystem.instance) {
      CollisionSystem.instance = new CollisionSystem();
    }
    return CollisionSystem.instance;
  }
  
  /**
   * Set the spatial manager reference for optimized collision detection
   */
  public setSpatialManager(spatialManager: SpatialManager): void {
    this.spatialManager = spatialManager;
    console.log('CollisionSystem connected to SpatialManager');
  }
  
  /**
   * Register a collidable object with the system
   */
  public registerObject(object: GameObject): void {
    if (!object.isCollidableObject()) {
      return; // Only register objects with bounding volumes
    }
    
    if (object.getType() === ObjectType.STATIC) {
      this.staticObjects.add(object);
    } else if (object instanceof DynamicObject) {
      this.dynamicObjects.add(object);
    }
  }
  
  /**
   * Unregister an object from the system
   */
  public unregisterObject(object: GameObject): void {
    if (object.getType() === ObjectType.STATIC) {
      this.staticObjects.delete(object);
    } else if (object instanceof DynamicObject) {
      this.dynamicObjects.delete(object);
    }
  }
  
  /**
   * Update the collision system - detect and resolve collisions
   */
  public update(deltaTime: number): void {
    // Update current time for collision cooldowns
    this.currentTime += deltaTime;
    
    // For each dynamic object, check for collisions with static objects
    this.dynamicObjects.forEach(dynamicObject => {
      // Skip non-moving or non-collidable objects
      if (!dynamicObject.isCollidableObject() || dynamicObject.getMovementState() !== 1) { // 1 = MOVING
        return;
      }
      
      // Get nearby objects for efficient collision detection
      const nearbyObjects = this.getNearbyObjects(dynamicObject);
      
      // Check collisions with static objects
      for (const staticObject of nearbyObjects) {
        if (dynamicObject.collidesWith(staticObject)) {
          // Handle collision
          this.handleCollision(dynamicObject, staticObject);
          break; // Only handle one collision per frame for simplicity
        }
      }
    });
  }
  
  /**
   * Get nearby objects that could potentially collide with the given object
   */
  private getNearbyObjects(object: GameObject): GameObject[] {
    if (this.spatialManager) {
      // Use spatial manager for efficient lookup
      return this.spatialManager.getObjectsInRadius(
        object.getPosition(),
        this.collisionCheckRadius
      ).filter(other => 
        other.isCollidableObject() && 
        other !== object && 
        other.getType() === ObjectType.STATIC
      );
    } else {
      // Fallback to checking all static objects
      return Array.from(this.staticObjects);
    }
  }
  
  /**
   * Handle collision between a dynamic object and a static object
   */
  private handleCollision(dynamicObject: DynamicObject, staticObject: GameObject): void {
    // Get the current velocity
    const velocity = dynamicObject.getVelocity().clone();
    
    // If no velocity, nothing to do
    if (velocity.lengthSq() < 0.0001) {
      return;
    }
    
    // Handle differently based on object type - use special handling for toasters
    if (dynamicObject instanceof LODToaster) {
      // Use toaster's custom collision handling
      (dynamicObject as LODToaster).handleCollision(this.currentTime, staticObject);
    } else {
      // Standard collision response - turn 90 degrees to the right
      // Get the direction to the static object
      const dynamicPos = dynamicObject.getPosition();
      const staticPos = staticObject.getPosition();
      
      // Direction from dynamic to static
      CollisionSystem.tempDir.subVectors(staticPos, dynamicPos).normalize();
      
      // Calculate avoidance direction (turn 90 degrees right)
      // Right is perpendicular to forward direction
      const rightVector = new THREE.Vector3(-CollisionSystem.tempDir.z, 0, CollisionSystem.tempDir.x);
      
      // Set the new velocity (maintain same speed but change direction)
      const speed = velocity.length();
      CollisionSystem.tempVelocity.copy(rightVector).multiplyScalar(speed);
      
      // Apply the new velocity to the object
      dynamicObject.setVelocity(
        CollisionSystem.tempVelocity.x,
        CollisionSystem.tempVelocity.y,
        CollisionSystem.tempVelocity.z
      );
    }
    
    // Log collision
    console.log(`Collision detected: ${dynamicObject.getName()} collided with ${staticObject.getName()}, changing direction`);
  }
  
  /**
   * Get statistics about the collision system
   */
  public getStats(): { staticObjects: number, dynamicObjects: number } {
    return {
      staticObjects: this.staticObjects.size,
      dynamicObjects: this.dynamicObjects.size
    };
  }
} 